/**
 * NinaDriveSyncModal — zentraler Sync für alle Projekte mit gdrive_folder_id.
 *
 * SSE-Stream basierte Fortschrittsanzeige. Pro Projekt eine Zeile mit Status,
 * Ordner-/Dateizählern und Laufzeit. Am Ende Zusammenfassung. Abbrechen möglich.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { ninaEngine } from "../lib/ninaEngineApi";

export default function NinaDriveSyncModal({ open, onClose, onFinished }) {
  const [running, setRunning] = useState(false);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const abortRef = useRef(null);

  const start = async () => {
    setRunning(true);
    setRows([]);
    setSummary(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await ninaEngine.driveSyncAllStream({
        signal: controller.signal,
        onStarted: (d) => setTotal(d?.total || 0),
        onProgress: (d) =>
          setRows((prev) => [
            ...prev,
            {
              index: d?.index,
              project_id: d?.project_id,
              project_name: d?.project_name || "?",
              status: d?.status,
              folders: d?.folders,
              files: d?.files,
              error: d?.error,
              elapsed_ms: d?.elapsed_ms,
              truncated: d?.truncated,
            },
          ]),
        onDone: (d) => {
          setSummary(d);
          setRunning(false);
          onFinished && onFinished(d);
        },
        onError: (err) => {
          toast.error(err?.message || "Sync fehlgeschlagen");
          setRunning(false);
        },
      });
    } catch (err) {
      // Wenn per abort → keine Toast
      if (err?.name !== "AbortError" && !controller.signal.aborted) {
        toast.error(err?.message || "Sync fehlgeschlagen");
      }
      setRunning(false);
    }
  };

  useEffect(() => {
    if (open && !running && rows.length === 0 && !summary) {
      // Automatisch starten sobald das Modal geöffnet wird
      start();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  if (!open) return null;

  const ok = rows.filter((r) => r.status === "ok").length;
  const err = rows.filter((r) => r.status === "error").length;
  const percent = total > 0 ? Math.min(100, Math.round((rows.length / total) * 100)) : 0;

  const handleAbort = () => {
    if (abortRef.current) abortRef.current.abort();
    setRunning(false);
    toast.info("Sync abgebrochen");
  };

  const handleClose = () => {
    if (running && !window.confirm("Sync läuft noch. Wirklich abbrechen und schließen?")) return;
    if (abortRef.current) abortRef.current.abort();
    onClose && onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={handleClose}
      data-testid="nina-sync-modal"
    >
      <div
        className="rounded-xl border max-w-2xl w-full max-h-[85vh] flex flex-col"
        style={{ borderColor: "var(--tga-border)", background: "var(--tga-surface)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 border-b"
              style={{ borderColor: "var(--tga-border)" }}>
          <div className="flex items-center gap-2">
            <RefreshCw
              className={`w-5 h-5 ${running ? "animate-spin" : ""}`}
              strokeWidth={1.7}
              style={{ color: "var(--tga-text-primary)" }}
            />
            <div>
              <div className="text-sm font-bold" style={{ color: "var(--tga-text-primary)" }}
                    data-testid="nina-sync-modal-title">
                Google Drive Synchronisierung
              </div>
              <div className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>
                {running
                  ? `Läuft — ${rows.length} von ${total || "?"} verarbeitet`
                  : summary
                  ? `Fertig — ${summary.ok_count} erfolgreich, ${summary.error_count} Fehler`
                  : "Wird gestartet …"}
              </div>
            </div>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded hover:opacity-70"
                    data-testid="nina-sync-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Fortschrittsbalken */}
        {total > 0 && (
          <div className="px-4 pt-3">
            <div className="h-2 rounded-full overflow-hidden"
                  style={{ background: "var(--tga-bg-soft, #f4f4f5)" }}>
              <div
                className="h-full transition-all"
                style={{
                  width: `${percent}%`,
                  background: err > 0 ? "#f59e0b" : "#10b981",
                }}
                data-testid="nina-sync-progress-bar"
              />
            </div>
            <div className="text-[11px] mt-1 flex justify-between"
                  style={{ color: "var(--tga-text-muted)" }}>
              <span>{percent}%</span>
              <span>
                <span style={{ color: "#059669" }}>{ok} ok</span>
                {err > 0 && <span style={{ color: "#b45309" }}> · {err} Fehler</span>}
              </span>
            </div>
          </div>
        )}

        {/* Zeilen */}
        <div className="flex-1 overflow-y-auto p-4 space-y-1">
          {rows.length === 0 && running && (
            <div className="text-sm text-center py-6 flex items-center justify-center gap-2"
                  style={{ color: "var(--tga-text-muted)" }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              Verbinde …
            </div>
          )}
          {rows.map((r) => (
            <div
              key={`${r.index}-${r.project_id}`}
              className="flex items-center gap-2 text-[13px] py-1.5 px-2 rounded"
              style={{
                background: r.status === "error" ? "#fef3c7" : "transparent",
                color: "var(--tga-text-primary)",
              }}
              data-testid="nina-sync-progress-row"
            >
              {r.status === "ok" ? (
                <CheckCircle2 className="w-4 h-4" style={{ color: "#10b981" }} />
              ) : (
                <AlertTriangle className="w-4 h-4" style={{ color: "#f59e0b" }} />
              )}
              <span className="font-medium truncate flex-1">{r.project_name}</span>
              {r.status === "ok" ? (
                <span className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>
                  {r.folders} Ordner · {r.files} Dateien
                  {r.truncated && " (gekürzt)"} · {(r.elapsed_ms / 1000).toFixed(1)} s
                </span>
              ) : (
                <span className="text-[11px]" style={{ color: "#b45309" }} title={r.error}>
                  {r.error?.slice(0, 60) || "Fehler"}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-3 border-t flex items-center gap-2"
              style={{ borderColor: "var(--tga-border)" }}>
          {running ? (
            <button
              onClick={handleAbort}
              className="text-sm px-3 py-1.5 rounded border"
              style={{ borderColor: "var(--tga-border)", color: "#991b1b" }}
              data-testid="nina-sync-abort-btn"
            >
              Abbrechen
            </button>
          ) : (
            <>
              <button
                onClick={start}
                className="text-sm px-3 py-1.5 rounded border"
                style={{ borderColor: "var(--tga-border)", color: "var(--tga-text-primary)" }}
                data-testid="nina-sync-rerun-btn"
              >
                Erneut synchronisieren
              </button>
              <div className="flex-1" />
              <button
                onClick={onClose}
                className="text-sm px-3 py-1.5 rounded border"
                style={{ borderColor: "var(--tga-border)",
                          background: "var(--tga-text-primary)", color: "var(--tga-surface)" }}
                data-testid="nina-sync-finish-btn"
              >
                Schließen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
